import React, { useState, useEffect } from 'react';
import { IoDocumentTextOutline, IoChevronBackOutline, IoChevronForwardOutline } from 'react-icons/io5';
import toast from 'react-hot-toast';
import api from '../services/api';
import { useTranslation } from 'react-i18next';

export default function DepositsContent() {
    const { t } = useTranslation();
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    const [deposits, setDeposits] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const today = new Date().toISOString().split('T')[0];
    const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    const [startDate, setStartDate] = useState(thirtyDaysAgo);
    const [endDate, setEndDate] = useState(today);
    const [isSearching, setIsSearching] = useState(false);

    useEffect(() => {
        fetchDeposits(startDate, endDate);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchDeposits = async (start, end) => {
        try {
            setLoading(true);
            setError(null);
            const result = await api.getDeposits(start, end);
            if (result.success) {
                setDeposits(result.data || []);
                setCurrentPage(1);
            } else {
                throw new Error(result.message || t('depositsContent.dataFetchError'));
            }
        } catch (err) {
            console.error('API Error:', err);
            setError(err.message || t('depositsContent.errorLoadingData'));
            toast.error(t('depositsContent.errorLoadingData'));
        } finally {
            setLoading(false);
        }
    };

    const handleApplyFilter = async () => {
        if (!startDate || !endDate) {
            toast.error(t('depositsContent.dateFilter.selectDatesError'));
            return;
        }
        if (new Date(startDate) > new Date(endDate)) {
            toast.error(t('depositsContent.dateFilter.invalidDateRangeError'));
            return;
        }
        setIsSearching(true);
        await fetchDeposits(startDate, endDate);
        setIsSearching(false);
    };

    const clearFilters = () => {
        setStartDate(thirtyDaysAgo);
        setEndDate(today);
        fetchDeposits(thirtyDaysAgo, today);
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleString('tr-TR', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getTypeLabel = (typeValue) => {
        const key = `depositsContent.types.${typeValue}`;
        const translated = t(key);
        return translated === key ? t('depositsContent.types.unknown') : translated;
    };

    const totalPages = Math.ceil(deposits.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentData = deposits.slice(startIndex, endIndex);

    return (
        <div className="crypto-withdrawals-content">
            <div className="change-password-header">
                <h1>{t('depositsContent.title')}</h1>
                <p>{t('depositsContent.subtitle')}</p>
            </div>

            <div className="date-filter-section">
                <div className="date-filter-container">
                    <div className="date-input-group">
                        <label htmlFor="depositsStartDate" className="date-label">
                            {t('depositsContent.dateFilter.startDateLabel')}
                        </label>
                        <input
                            type="date"
                            id="depositsStartDate"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            max={endDate}
                            className="date-input"
                        />
                    </div>
                    <div className="date-input-group">
                        <label htmlFor="depositsEndDate" className="date-label">
                            {t('depositsContent.dateFilter.endDateLabel')}
                        </label>
                        <input
                            type="date"
                            id="depositsEndDate"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            min={startDate}
                            max={today}
                            className="date-input"
                        />
                    </div>
                    <div className="date-filter-actions">
                        <button
                            onClick={handleApplyFilter}
                            disabled={isSearching || !startDate || !endDate}
                            className="filter-btn"
                        >
                            {isSearching ? (
                                <>
                                    <div className="spinner"></div>
                                    {t('depositsContent.dateFilter.searching')}
                                </>
                            ) : (
                                t('depositsContent.dateFilter.filterButton')
                            )}
                        </button>
                        <button
                            onClick={clearFilters}
                            className="clear-btn"
                        >
                            {t('depositsContent.dateFilter.clearButton')}
                        </button>
                    </div>
                </div>
            </div>

            <div className="content-body">
                {loading ? (
                    <div className="loading-container">
                        <div className="spinner"></div>
                        <p>{t('depositsContent.loading')}</p>
                    </div>
                ) : error ? (
                    <div className="error-container">
                        <p>{t('depositsContent.error')} {error}</p>
                        <button onClick={() => fetchDeposits(startDate, endDate)} className="retry-btn">
                            {t('depositsContent.retryButton')}
                        </button>
                    </div>
                ) : (
                    <div className="table-container">
                        {currentData.length === 0 ? (
                            <div className="no-data">
                                <div className="no-data-content">
                                    <IoDocumentTextOutline size={48} />
                                    <p>{t('depositsContent.noDataMessage')}</p>
                                </div>
                            </div>
                        ) : (
                            <div className="div-table">
                                <div className="div-table-header">
                                    <div className="div-table-cell">{t('depositsContent.tableHeaders.date')}</div>
                                    <div className="div-table-cell">{t('depositsContent.tableHeaders.type')}</div>
                                    <div className="div-table-cell">{t('depositsContent.tableHeaders.amount')}</div>
                                    <div className="div-table-cell">{t('depositsContent.tableHeaders.orderId')}</div>
                                    <div className="div-table-cell">{t('depositsContent.tableHeaders.comment')}</div>
                                </div>

                                <div className="div-table-body">
                                    {currentData.map((item, idx) => (
                                        <div key={item.orderID || idx} className="div-table-row">
                                            <div className="div-table-cell" data-label={t('depositsContent.tableHeaders.date')}>
                                                <div className="date-info">
                                                    <span className="date">{formatDate(item.time)}</span>
                                                </div>
                                            </div>
                                            <div className="div-table-cell" data-label={t('depositsContent.tableHeaders.type')}>
                                                <div className="coin-info">
                                                    <span className="coin-symbol">{getTypeLabel(item.type)}</span>
                                                </div>
                                            </div>
                                            <div className="div-table-cell" data-label={t('depositsContent.tableHeaders.amount')}>
                                                <div className="amount-info">
                                                    <span className="amount">{Number(item.amount ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                                </div>
                                            </div>
                                            <div className="div-table-cell" data-label={t('depositsContent.tableHeaders.orderId')}>
                                                <div className="address-info">
                                                    <span className="address">{item.orderID || '-'}</span>
                                                </div>
                                            </div>
                                            <div className="div-table-cell" data-label={t('depositsContent.tableHeaders.comment')}>
                                                <div className="address-info">
                                                    <span className="address">{item.comment || '-'}</span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {totalPages > 1 && (
                            <div className="pagination-container">
                                <div className="pagination">
                                    <button
                                        className="pagination-btn prev-btn"
                                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                        disabled={currentPage === 1}
                                    >
                                        <IoChevronBackOutline size={16} />
                                    </button>

                                    {Array.from({ length: Math.min(3, totalPages - currentPage + 1) }, (_, i) => {
                                        const page = currentPage + i;
                                        if (page > totalPages) return null;
                                        return (
                                            <button
                                                key={page}
                                                className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
                                                onClick={() => setCurrentPage(page)}
                                            >
                                                {page}
                                            </button>
                                        );
                                    })}

                                    <button
                                        className="pagination-btn next-btn"
                                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                        disabled={currentPage === totalPages}
                                    >
                                        <IoChevronForwardOutline size={16} />
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
